import { IController } from 'angular';
import * as _ from 'lodash';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
import { CurrentSchoolYear } from '../../../../../../../../ng2/shared/constants/current-school-year.constant';
import { IStudent } from '../../../../../../../../ng2/shared/typings/interfaces/student.interface';
import { CreditGapsPriorityGroupingsService } from '../../../../../../../shared/services/credit-gaps-priority-groupings/credit-gaps-priority-groupings.service';
import { IWizardFilterActionsOptions } from '../../../../../components/wizard-filter-actions/wizard-filter-actions.typings';
import { WizardUtils } from './../../../../../../../shared/services/wizard-utils/wizard-utils.service';

export class ReviewGapsByPriorityStepCtrl implements IController {
  static inject: string[] = ['$scope', 'ImSchool', 'CreditGapsPriorityGroupingsService', 'WizardUtils'];

  public currentFilter: any;
  public currentLabel: string;
  public currentStudentYear;
  public priorityGroups;
  public sort: any;
  public plurals;

  private buckets;
  private currentStudents: any[];

  public activeStudentsIds: string[];
  public activeSuperSeniorsIds: string[];
  public activeWithUnaddressedGapsIds: string[];
  public activeWithUnaddressedGapsFull: IStudent[];
  public activeWithAddressedGapsIds: string[];
  public activeWithNoGapsIds: string[];

  public countActiveStudents: number;
  public countActiveSuperSeniors: number;
  public countActiveWithUnaddressedGaps: number;
  public countActiveWithAddressedGaps: number;
  public countActiveWithNoGaps: number;

  public currentCohortFilter;
  public options: IWizardFilterActionsOptions;

  constructor(
    public $scope,
    private ImSchool: ImSchool,
    private CreditGapsPriorityGroupingsService: CreditGapsPriorityGroupingsService,
    private WizardUtils: WizardUtils,
  ) {
    this.options = {
      columnKeys: ['gradPlan', 'creditMaxGaps', 'creditMaxGapsNoPlan'],
    };
  }

  $onInit(): void {
    this.WizardUtils.startListeningAndLoad(this);
  }

  setUi(): void {
    this.currentFilter = this.WizardUtils.getEntireSdcFilter();
    this.currentCohortFilter = _.last(this.currentFilter.cohort);
    this.currentStudentYear = this.getCurrentStudentYear();
    this.currentLabel = this.getLabel();
    this.currentStudents = this.WizardUtils.getFilteredFlattenedStudents();
    this.buckets = this.generateFilteredStudentIdBuckets();
    this.activeStudentsIds = this.buckets.activeStudentsIds;
    this.activeSuperSeniorsIds = this.buckets.activeSuperSeniorsIds;
    this.activeWithUnaddressedGapsIds = this.buckets.activeWithUnaddressedGapsIds;
    this.activeWithUnaddressedGapsFull = this.buckets.activeWithUnaddressedGapsFull;
    this.activeWithAddressedGapsIds = this.buckets.activeWithAddressedGapsIds;
    this.activeWithNoGapsIds = this.buckets.activeWithNoGapsIds;
    this.countActiveSuperSeniors = this.activeSuperSeniorsIds.length;
    this.countActiveWithUnaddressedGaps = this.activeWithUnaddressedGapsIds.length;
    this.countActiveWithAddressedGaps = this.activeWithAddressedGapsIds.length;
    this.countActiveWithNoGaps = this.activeWithNoGapsIds.length;
    this.priorityGroups = this.generatePriorityGroups(this.activeWithUnaddressedGapsFull);

    this.sort = { sort: { colId: 'studentName', sort: 'asc' } };

    this.plurals = {
      student: {
        0: 'students',
        1: 'student',
        other: 'students',
      },
      has: {
        0: 'have',
        1: 'has',
        other: 'have',
      },
      is: {
        0: 'are',
        1: 'is',
        other: 'are',
      },
    };
  }

  private getCurrentStudentYear() {
    return this.ImSchool.getStudentYearForCohort(this.currentCohortFilter);
  }

  hasSuperSeniors(): boolean {
    return this.currentCohortFilter && this.currentCohortFilter === CurrentSchoolYear.ENDFULL;
  }

  private getLabel() {
    const status = this.hasSuperSeniors();
    return status === true ? `${this.currentCohortFilter} and older` : this.currentCohortFilter;
  }

  private generateFilteredStudentIdBuckets(): object {
    const activeStudentsIds = [];
    const activeSuperSeniorsIds = [];
    const activeWithUnaddressedGapsIds = [];
    const activeWithUnaddressedGapsFull = [];
    const activeWithAddressedGapsIds = [];
    const activeWithNoGapsIds = [];

    _.each(this.currentStudents, (student: any) => {
      const { studentId, cohort, status, creditMaxGaps, creditMaxGapsNoPlan, isSuperSenior } = student;
      const isActive = status === 'A';
      // if `All` is selected in the dropdown, all students are `inCohort`
      let inCohort = true;
      if (this.currentCohortFilter) {
        if (isSuperSenior) inCohort = Number(cohort) <= Number(this.currentCohortFilter);
        inCohort = cohort === this.currentCohortFilter;
      }
      const hasUnaddressedGaps = creditMaxGapsNoPlan < 0;
      const hasAddressedGaps = creditMaxGaps < 0 && creditMaxGapsNoPlan >= 0;
      const hasNoGaps = creditMaxGaps === 0;

      if (isActive && inCohort) activeStudentsIds.push(studentId);
      if (isActive && isSuperSenior) activeSuperSeniorsIds.push(studentId);
      if (isActive && hasUnaddressedGaps) {
        _.pull(activeWithAddressedGapsIds, studentId);
        activeWithUnaddressedGapsIds.push(studentId);
        activeWithUnaddressedGapsFull.push(student);
      }
      if (isActive && hasAddressedGaps) {
        _.pull(activeWithUnaddressedGapsIds, studentId);
        _.pull(activeWithUnaddressedGapsFull, student);
        activeWithAddressedGapsIds.push(studentId);
      }
      if (isActive && hasNoGaps) activeWithNoGapsIds.push(studentId);
    });

    return {
      activeStudentsIds,
      activeSuperSeniorsIds,
      activeWithUnaddressedGapsIds,
      activeWithUnaddressedGapsFull,
      activeWithAddressedGapsIds,
      activeWithNoGapsIds,
    };
  }

  private generatePriorityGroups(students) {
    const priorityGroups = this.CreditGapsPriorityGroupingsService.getPriorityGroupsByStudentYearFilter(
      this.currentStudentYear,
    );
    _.each(priorityGroups, (group: any) => (group.studentIds = []));
    _.each(students, (student: any) => {
      const { studentId, creditGapPriorityGrouping } = student;
      _.each(priorityGroups, (group: any) => {
        if (group.studentPriorityGroupingValue === creditGapPriorityGrouping) {
          group.studentIds.push(studentId);
        }
      });
    });
    return priorityGroups;
  }
}
